import { useCallback } from "react";
import { Button } from "@shop-ledger/ui-components";
import { fetchLiveHealthNodes } from "../../api/healthApi";
import { usePlatformClient } from "../../api/ClientContext";
import { MockBadge } from "../../components/MockBadge";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import type { HealthNode, HealthStatus } from "../../api/types";

const STATUS_LABEL: Record<HealthStatus, string> = {
  healthy: "Healthy",
  degraded: "Degraded",
  down: "Down",
  unknown: "Unknown",
};

function StatusDot({ status }: { status: HealthStatus }) {
  return <span className={`status-dot status-dot--${status}`} aria-hidden="true" />;
}

/**
 * §9.3's dependency-map status grid. Merges TWO sources per render:
 *  1. `fetchLiveHealthNodes()` (`api/healthApi.ts`) — REAL fetches against the running
 *     backend's `/healthz` and `/readyz`. If no backend is reachable from this browser,
 *     these nodes correctly show `unknown`/`down`, not a faked "healthy."
 *  2. `client.listHealthNodes()` — the mocked remainder of §9.3's dependency map (MinIO
 *     nodes, replication lag, backup age, nginx cache, worker queue depth) — nothing in
 *     `backend/` exposes any of these signals yet.
 * Every mocked node carries a small `MockBadge`; the two live nodes do not.
 */
export function SystemHealthPage() {
  const client = usePlatformClient();

  const fetchAll = useCallback(async (): Promise<HealthNode[]> => {
    const [live, mocked] = await Promise.all([fetchLiveHealthNodes(), client.listHealthNodes()]);
    return [...live, ...mocked];
  }, [client]);

  const { data, loading, error, reload } = useAsyncData(fetchAll, [fetchAll]);

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">System Health</h1>
          <p className="page__subtitle">
            A dependency map, rendered as a status grid. The first two nodes below are live calls to this
            backend's real <code>/healthz</code>/<code>/readyz</code> routes; every other node is simulated.
          </p>
        </div>
        <Button type="button" variant="secondary" size="md" onClick={reload} disabled={loading}>
          {loading ? "Checking…" : "Re-check now"}
        </Button>
      </div>

      {error ? <StatusBanner kind="error">{error}</StatusBanner> : null}

      {data ? (
        <div className="health-grid">
          {data.map((node) => (
            <div key={node.id} className={`health-node health-node--${node.status}`}>
              <div className="health-node__header">
                <span className="health-node__label">
                  <StatusDot status={node.status} />
                  {node.label}
                </span>
                <MockBadge live={!node.mocked} />
              </div>
              <div className="muted">{STATUS_LABEL[node.status]}</div>
              <div className="muted">{node.detail}</div>
              <div className="muted" style={{ fontSize: "0.72rem", marginTop: 4 }}>
                checked {new Date(node.lastChecked).toLocaleTimeString()}
              </div>
            </div>
          ))}
        </div>
      ) : loading ? (
        <p className="muted">Loading health signals…</p>
      ) : null}
    </div>
  );
}
